import { useEffect, useState } from "react";
import { ArrowUp, CalendarRange, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { inputCls, btnPrimary, Skeleton } from "@/components/portal/ui";
import { Switch } from "@/components/ui/switch";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { ImageField } from "@/pages/admin/ResourceManager";

const GROUPS = [
  ["Hero (bagian paling atas)", [
    ["hero_badge", "Badge Hero"], ["hero_title_1", "Judul Hero Baris 1"], ["hero_title_2", "Judul Hero Baris 2"],
    ["hero_subtitle", "Sub Teks Hero", "textarea"], ["hero_cta_primary", "Tombol Utama"], ["hero_cta_secondary", "Tombol Kedua"],
  ]],
  ["Gembala & Tentang", [
    ["pastor_photo", "Foto Gembala", "image"], ["pastor_name", "Nama Gembala"], ["pastor_title", "Jabatan"],
    ["pastor_quote", "Kutipan", "textarea"], ["about_label", "Label Section Tentang"], ["about_heading", "Judul Tentang"],
    ["about_text", "Teks Tentang", "textarea"], ["vision", "Visi"], ["mission", "Misi", "textarea"],
  ]],
  ["Section NIG", [["nig_label", "Label Section"], ["nig_heading", "Judul"], ["nig_text", "Teks", "textarea"]]],
  ["Jadwal & Event", [
    ["schedule_label", "Label Jadwal"], ["schedule_heading", "Judul Jadwal"],
    ["special_label", "Label Event Khusus"], ["worship_label", "Label Ibadah & Pengumuman"],
    ["events_label", "Label Kegiatan"], ["events_heading", "Judul Kegiatan"],
  ]],
  ["Sermon & Renungan", [
    ["sermon_label", "Label Sermon"], ["sermon_heading_1", "Judul Sermon Baris 1"], ["sermon_heading_2", "Judul Sermon Baris 2"],
    ["devotion_label", "Label Renungan"],
  ]],
  ["Relationship Community (RC)", [
    ["community_label", "Label Section"], ["community_heading_1", "Judul Baris 1"], ["community_heading_2", "Judul Baris 2"],
    ["community_text", "Teks", "textarea"],
  ]],
  ["Galeri & Pastoral", [
    ["gallery_label", "Label Galeri"], ["gallery_heading_1", "Judul Galeri Baris 1"], ["gallery_heading_2", "Judul Galeri Baris 2"],
    ["pastoral_label", "Label Pastoral"], ["pastoral_heading_1", "Judul Pastoral Baris 1"],
    ["pastoral_heading_2", "Judul Pastoral Baris 2"], ["pastoral_text", "Teks Pastoral", "textarea"],
  ]],
];

function HeroImages({ list, onChange }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid="hero-images-field">
      <p className="font-extrabold text-slate-900">Gambar Latar Hero (di belakang tulisan GIM Church Makassar)</p>
      <p className="text-xs text-slate-500 mt-1">Bisa lebih dari satu — gambar akan berganti otomatis setiap 7 detik di website.</p>
      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        {list.map((src, i) => (
          <div key={src + i} className="relative rounded-2xl overflow-hidden border border-slate-100 aspect-video" data-testid={`hero-image-${i}`}>
            <img src={fileUrl(src)} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-2 right-2 flex gap-1">
              {i > 0 && <button type="button" onClick={() => { const l = [...list]; [l[i - 1], l[i]] = [l[i], l[i - 1]]; onChange(l); }} data-testid={`hero-image-up-${i}`} className="w-7 h-7 rounded-lg bg-white/90 grid place-items-center text-slate-600"><ArrowUp className="w-3.5 h-3.5" /></button>}
              <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} data-testid={`hero-image-remove-${i}`} className="w-7 h-7 rounded-lg bg-white/90 grid place-items-center text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid md:grid-cols-2 gap-4 items-start">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Unggah gambar baru</p>
          <div className="mt-2"><ImageField value="" id="hero-upload" onChange={(v) => v && onChange([...list, v])} /></div>
        </div>
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">atau tempel URL gambar</p>
          <div className="mt-2 flex gap-2">
            <input className={inputCls} placeholder="https://…" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="hero-image-url-input" />
            <button type="button" disabled={!draft.trim()} onClick={() => { onChange([...list, draft.trim()]); setDraft(""); }} data-testid="hero-image-add" className="bg-slate-900 text-white rounded-xl px-4 text-sm font-bold disabled:opacity-40"><Plus className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

const uid = () => (crypto?.randomUUID?.() || `s_${Date.now()}${Math.random().toString(36).slice(2, 6)}`);
const yr = new Date().getFullYear();
const PRESETS = [
  { name: "Natal", badge: "🎄 Selamat Natal", start: `${yr}-12-01`, end: `${yr}-12-27`, recurring: true },
  { name: "Paskah", badge: "✝️ Selamat Paskah", start: "", end: "", recurring: false },
  { name: "Tahun Baru", badge: "🎉 Tahun Baru", start: `${yr}-12-28`, end: `${yr + 1}-01-05`, recurring: true },
];

function SeasonImages({ list, onChange, id }) {
  const [draft, setDraft] = useState("");
  return (
    <div>
      <div className="grid sm:grid-cols-4 gap-3">
        {list.map((src, i) => (
          <div key={src + i} className="relative rounded-xl overflow-hidden border border-slate-100 aspect-video" data-testid={`${id}-img-${i}`}>
            <img src={fileUrl(src)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} data-testid={`${id}-img-remove-${i}`} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-lg bg-white/90 grid place-items-center text-red-600"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 grid md:grid-cols-2 gap-3 items-start">
        <ImageField value="" id={`${id}-upload`} onChange={(v) => v && onChange([...list, v])} />
        <div className="flex gap-2">
          <input className={inputCls} placeholder="atau tempel URL gambar" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`${id}-url-input`} />
          <button type="button" disabled={!draft.trim()} onClick={() => { onChange([...list, draft.trim()]); setDraft(""); }} data-testid={`${id}-url-add`} className="bg-slate-900 text-white rounded-xl px-4 text-sm font-bold disabled:opacity-40"><Plus className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function HeroSeasons({ list, onChange }) {
  const update = (i, patch) => onChange(list.map((s, x) => (x === i ? { ...s, ...patch } : s)));
  const add = (preset) => onChange([...list, { id: uid(), name: "", badge: "", start: "", end: "", recurring: false, images: [], ...(preset || {}) }]);
  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid="hero-seasons-field">
      <div className="flex items-center gap-2"><CalendarRange className="w-4 h-4 text-blue-600" /><p className="font-extrabold text-slate-900">Galeri Hero Musiman</p></div>
      <p className="text-xs text-slate-500 mt-1">Buat koleksi gambar khusus (mis. Natal, Paskah). Saat tanggalnya aktif, gambar & teks hero di beranda otomatis berganti ke suasana musim itu. Aktifkan "Berulang tiap tahun" agar tahun berikutnya berjalan otomatis (tahun pada tanggal diabaikan).</p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="hero-season-presets">
        {PRESETS.map((p) => (
          <button type="button" key={p.name} onClick={() => add(p)} data-testid={`hero-season-preset-${p.name.toLowerCase().replace(/\s/g, "-")}`} className="inline-flex items-center gap-1.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5 transition"><Sparkles className="w-3.5 h-3.5" /> {p.name}</button>
        ))}
        <button type="button" onClick={() => add()} data-testid="hero-season-add-custom" className="inline-flex items-center gap-1.5 text-xs font-bold bg-blue-600 text-white rounded-full px-3 py-1.5 hover:bg-blue-700 transition"><Plus className="w-3.5 h-3.5" /> Musim Baru</button>
      </div>
      <div className="mt-5 space-y-5">
        {list.map((s, i) => (
          <div key={s.id || i} className="rounded-2xl border border-slate-200 p-5" data-testid={`hero-season-${i}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid sm:grid-cols-2 gap-3 flex-1">
                <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Nama musim</span>
                  <input className={`${inputCls} mt-1`} placeholder="mis. Natal" value={s.name || ""} onChange={(e) => update(i, { name: e.target.value })} data-testid={`hero-season-name-${i}`} /></label>
                <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Badge / teks hero</span>
                  <input className={`${inputCls} mt-1`} placeholder="mis. 🎄 Selamat Natal" value={s.badge || ""} onChange={(e) => update(i, { badge: e.target.value })} data-testid={`hero-season-badge-${i}`} /></label>
                <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Mulai</span>
                  <input type="date" className={`${inputCls} mt-1`} value={s.start || ""} onChange={(e) => update(i, { start: e.target.value })} data-testid={`hero-season-start-${i}`} /></label>
                <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Selesai</span>
                  <input type="date" className={`${inputCls} mt-1`} value={s.end || ""} onChange={(e) => update(i, { end: e.target.value })} data-testid={`hero-season-end-${i}`} /></label>
              </div>
              <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} data-testid={`hero-season-remove-${i}`} className="w-8 h-8 shrink-0 rounded-lg bg-slate-100 text-red-600 grid place-items-center hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Switch checked={!!s.recurring} onCheckedChange={(v) => update(i, { recurring: v })} data-testid={`hero-season-recurring-${i}`} />
              <span className="text-xs font-semibold text-slate-600">Berulang tiap tahun</span>
            </div>
            <div className="mt-4">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Gambar musim ini</span>
              <div className="mt-2"><SeasonImages id={`hero-season-${i}`} list={s.images || []} onChange={(imgs) => update(i, { images: imgs })} /></div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-slate-400 py-2">Belum ada musim. Pilih preset di atas atau tambah musim baru.</p>}
      </div>
    </div>
  );
}

function Milestones({ list, onChange }) {
  const update = (i, patch) => onChange(list.map((m, x) => (x === i ? { ...m, ...patch } : m)));
  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid="milestones-field">
      <p className="font-extrabold text-slate-900">Milestone / Perjalanan GIM</p>
      <p className="text-xs text-slate-500 mt-1">Tonggak sejarah yang tampil di bagian "Tentang" pada beranda website. Urut dari atas ke bawah.</p>
      <div className="mt-4 space-y-3">
        {list.map((m, i) => (
          <div key={i} className="flex gap-2 items-start" data-testid={`milestone-${i}`}>
            <input className={`${inputCls} !w-24 shrink-0`} placeholder="Tahun" value={m.year || ""} onChange={(e) => update(i, { year: e.target.value })} data-testid={`milestone-year-${i}`} />
            <textarea rows={2} className={inputCls} placeholder="Keterangan peristiwa" value={m.text || ""} onChange={(e) => update(i, { text: e.target.value })} data-testid={`milestone-text-${i}`} />
            <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} data-testid={`milestone-remove-${i}`} className="w-9 h-9 shrink-0 rounded-lg bg-slate-100 text-red-600 grid place-items-center hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-slate-400">Belum ada milestone.</p>}
      </div>
      <button type="button" onClick={() => onChange([...list, { year: "", text: "" }])} data-testid="milestone-add" className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold bg-blue-600 text-white rounded-full px-3 py-1.5 hover:bg-blue-700 transition"><Plus className="w-3.5 h-3.5" /> Tambah Milestone</button>
    </div>
  );
}

function People({ id, title, hint, roleLabel, list, onChange, labelField, headingField, s, setS }) {
  const update = (i, patch) => onChange(list.map((m, x) => (x === i ? { ...m, ...patch } : m)));
  const isPastor = id === "pastors";
  const blank = isPastor ? { photo: "", name: "", title: "", description: "" } : { photo: "", name: "", role: "", description: "" };
  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid={`${id}-field`}>
      <p className="font-extrabold text-slate-900">{title}</p>
      <p className="text-xs text-slate-500 mt-1">{hint}</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Label Section</span>
          <input className={`${inputCls} mt-1`} value={s[labelField] || ""} onChange={(e) => setS({ ...s, [labelField]: e.target.value })} data-testid={`${id}-section-label`} /></label>
        <label className="block"><span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Judul Section</span>
          <input className={`${inputCls} mt-1`} value={s[headingField] || ""} onChange={(e) => setS({ ...s, [headingField]: e.target.value })} data-testid={`${id}-section-heading`} /></label>
      </div>
      <div className="mt-4 space-y-4">
        {list.map((m, i) => (
          <div key={i} className="grid md:grid-cols-[9rem_1fr] gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100" data-testid={`${id}-item-${i}`}>
            <div><ImageField value={m.photo} onChange={(v) => update(i, { photo: v })} id={`${id}-photo-${i}`} /></div>
            <div className="space-y-2">
              <input className={inputCls} placeholder="Nama" value={m.name || ""} onChange={(e) => update(i, { name: e.target.value })} data-testid={`${id}-name-${i}`} />
              <input className={inputCls} placeholder={roleLabel} value={(isPastor ? m.title : m.role) || ""} onChange={(e) => update(i, isPastor ? { title: e.target.value } : { role: e.target.value })} data-testid={`${id}-role-${i}`} />
              <textarea rows={3} className={inputCls} placeholder="Deskripsi (tampil di samping foto)" value={m.description || ""} onChange={(e) => update(i, { description: e.target.value })} data-testid={`${id}-desc-${i}`} />
              <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} data-testid={`${id}-remove-${i}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 bg-red-50 rounded-full px-3 py-1.5 hover:bg-red-100"><Trash2 className="w-3.5 h-3.5" /> Hapus</button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-slate-400">Belum ada data. Klik "Tambah" untuk menambah.</p>}
      </div>
      <button type="button" onClick={() => onChange([...list, blank])} data-testid={`${id}-add`} className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold bg-blue-600 text-white rounded-full px-3 py-1.5 hover:bg-blue-700 transition"><Plus className="w-3.5 h-3.5" /> Tambah</button>
    </div>
  );
}

export default function SiteContent() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/public/site").then((r) => setS(r.data));
  }, []);
  if (!s) return <Skeleton className="h-96" />;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/site", s);
      toast.success("Konten beranda diperbarui");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="admin-site-content">
      <AdminTitle eyebrow="Landing Page" title="Konten Beranda">
        <button disabled={busy} data-testid="site-save-button" className={btnPrimary}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Konten"}</button>
      </AdminTitle>
      <div className="mb-6 bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100" data-testid="site-branding-card">
        <p className="font-extrabold text-slate-900">Logo & Favicon Aplikasi</p>
        <p className="text-xs text-slate-500 mt-1">Logo tampil di beranda, portal jemaat, dan halaman login. Favicon adalah ikon kecil di tab browser. Gunakan PNG transparan (logo) dan gambar persegi (favicon). Kosongkan untuk memakai logo bawaan.</p>
        <div className="mt-5 grid md:grid-cols-2 gap-6">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Logo Aplikasi</span>
            <div className="mt-2"><ImageField value={s.logo_url} onChange={(v) => setS({ ...s, logo_url: v })} id="logo_url" /></div>
            <div className="mt-3 rounded-2xl bg-slate-900 p-4 inline-flex items-center gap-3" data-testid="logo-preview-dark">
              <img src={s.logo_url ? fileUrl(s.logo_url) : "/logo-gim.png"} alt="pratinjau logo" className="h-8 w-auto" style={{ filter: "invert(1) brightness(2)" }} />
              <span className="text-white/40 text-xs font-semibold">pratinjau latar gelap</span>
            </div>
          </div>
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Favicon (ikon tab browser)</span>
            <div className="mt-2"><ImageField value={s.favicon_url} onChange={(v) => setS({ ...s, favicon_url: v })} id="favicon_url" /></div>
            <p className="mt-3 text-[11px] text-slate-400">Perubahan favicon akan terlihat setelah menyimpan dan menyegarkan halaman (browser kadang menyimpan ikon lama di cache).</p>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {GROUPS.map(([group, fields]) => (
            <div key={group} className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-4" data-testid={`site-group-${group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="font-extrabold text-slate-900">{group}</p>
              {fields.map(([k, label, type]) => (
                <label key={k} className="block">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
                  <div className="mt-2">
                    {type === "image" ? <ImageField value={s[k]} onChange={(v) => setS({ ...s, [k]: v })} id={k} />
                      : type === "textarea" ? <textarea rows={3} className={inputCls} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`site-${k}`} />
                      : <input className={inputCls} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`site-${k}`} />}
                  </div>
                </label>
              ))}
            </div>
          ))}
          <p className="text-xs text-slate-500">Rekening transfer diatur di menu Pengaturan. Teks Layar Sapa Jemaat diatur di menu Layar Sapa Jemaat.</p>
        </div>
        <div className="relative rounded-[2rem] overflow-hidden bg-slate-900 aspect-[4/5] lg:sticky lg:top-8" data-testid="site-preview">
          <img src={fileUrl(s.pastor_photo)} alt="" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
          <div className="absolute bottom-0 p-8 text-white">
            <span className="text-[11px] font-bold uppercase tracking-widest bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-3 py-1">{s.pastor_title}</span>
            <p className="mt-4 text-3xl font-black tracking-tight">{s.pastor_name}</p>
            <p className="mt-3 text-slate-300 italic">"{s.pastor_quote}"</p>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <HeroImages list={s.hero_images || []} onChange={(l) => setS({ ...s, hero_images: l })} />
      </div>
      <div className="mt-6">
        <Milestones list={s.milestones || []} onChange={(l) => setS({ ...s, milestones: l })} />
      </div>
      <div className="mt-6">
        <People id="pastors" title="Our Pastor" roleLabel="Jabatan" s={s} setS={setS}
          hint="Daftar gembala/pendeta yang tampil di section Our Pastor (foto di samping deskripsi). Tampil di menu atas website."
          list={s.pastors || []} onChange={(l) => setS({ ...s, pastors: l })}
          labelField="pastor_section_label" headingField="pastor_section_heading" />
      </div>
      <div className="mt-6">
        <People id="team" title="Our Team" roleLabel="Peran / Jabatan" s={s} setS={setS}
          hint="Daftar anggota tim pelayanan yang tampil di section Our Team. Tambahkan foto, nama, peran, dan deskripsi."
          list={s.team || []} onChange={(l) => setS({ ...s, team: l })}
          labelField="team_section_label" headingField="team_section_heading" />
      </div>
      <div className="mt-6">
        <HeroSeasons list={s.hero_seasons || []} onChange={(l) => setS({ ...s, hero_seasons: l })} />
      </div>
    </form>
  );
}
